import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Grid, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { chunk, entries, isNil, orderBy, uniqBy } from 'lodash'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import Select from '../../../../components/forms/Select'
import Checkbox from '../../../../components/forms/Checkbox'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import Button from '../../../../components/forms/Button'
import DtoPluviometerStats from '../../../dto/DtoPluviometerStats'
import { getFrequencyStats } from '../../../../piezometry/components/suivi/utils/SuiviStatsUtils'
import {
    EFFICIENT_RAIN,
    GENERIC,
    MONTHS_STACK,
    NORMAL_COMPARISON,
    NORMALS_MONTHS,
    NORMALS_YEARS,
    STATS_TAB,
} from '../constants/PluvioChartTypes'

const ChoiceTypeChart = ({
    typeChart,
    setTypeChart,
    pluviometerStatistics,
    stat,
    setStat,
    dateComparedNormal,
    setDateComparedNormal,
    showEnveloppe,
    setShowEnveloppe,
    paramsFrequencyStats,
    setParamsFrequencyStats,
    envelopeGroupLabel,
    envelopeGroup = 'auto',
    setEnvelopeGroup = () => {},
    tab,
}) => {
    const [newTypeChart, setNewTypeChart] = useState(typeChart)
    const [newStat, setNewStat] = useState(stat)
    const [tmpDateComparedNormal, setTmpDateComparedNormal] = useState(dateComparedNormal)
    const [isValidateActive, setIsValidateActive] = useState(false)
    const [isShowSeriesObjet, setIsShowSeriesObjet] = useState({})

    useEffect(() => {
        setNewStat(stat)
    }, [stat])

    const ENVELOPE_GROUP_OPTIONS = [
        { value: 'auto', label: i18n.auto },
        { value: 'sumHour', label: i18n.hourTotal },
        { value: 'sumDay', label: i18n.dailyTotal },
        { value: 'sumWeek', label: i18n.weeklyTotal },
        { value: 'sumDecade', label: i18n.decadeTotal },
        { value: 'sumMonth', label: i18n.monthlyTotal },
        { value: 'sumYear', label: i18n.yearTotal },
    ]

    const typesGraph = [
        { value: NORMALS_MONTHS, label: i18n.normalMonths },
        { value: NORMALS_YEARS, label: i18n.normalYears },
        { value: GENERIC, label: i18n.graphicTracking },
        { value: MONTHS_STACK, label: i18n.monthsStacked },
        { value: EFFICIENT_RAIN, label: i18n.effectiveRain },
        { value: NORMAL_COMPARISON, label: i18n.comparisonNormal },
    ]

    const onSave = () => {
        setTypeChart(newTypeChart)
        setStat(newStat)
        setDateComparedNormal(tmpDateComparedNormal)
        setIsValidateActive(false)
    }

    const onSaveFrequencyStats = () => {
        const activeModes = entries(isShowSeriesObjet)
            .filter(([, isActive]) => isActive)
            .map(([mode]) => mode)
        setParamsFrequencyStats(prev => ({ ...prev, showPreviousYears: activeModes }))
    }

    const change = (subStat, v) => {
        setIsShowSeriesObjet(prev => ({ ...prev, [subStat]: v }))
    }

    if (tab !== STATS_TAB) return null

    const freChecksHeight = chunk(getFrequencyStats(paramsFrequencyStats.histoYears), 2).map(([freS, freH]) => (
        <Grid container spacing={1} key={freS.code}>
            <Grid size={6}>
                <Checkbox
                    label={freS.name}
                    checked={isShowSeriesObjet[freS.code]}
                    onChange={v => change(freS.code, v)}
                />
            </Grid>
            <Grid size={6}>
                <Checkbox
                    label={freH.name}
                    checked={isShowSeriesObjet[freH.code]}
                    onChange={v => change(freH.code, v)}
                />
            </Grid>
        </Grid>
    ))

    const disabledButton = !isValidateActive || (newTypeChart === GENERIC && isNil(newStat))

    return (
        <>
            <StyledFieldSet>
                <StyledLegend>{i18n.chartOptions}</StyledLegend>
                <Grid container spacing={1}>
                    <Grid size={4}>
                        <Select
                            label={i18n.graphicType}
                            options={typesGraph}
                            value={newTypeChart}
                            onChange={t => {
                                setNewTypeChart(t)
                                setIsValidateActive(true)
                            }}
                            noNullValue
                        />
                    </Grid>
                    {newTypeChart === GENERIC && (
                        <Grid size={4}>
                            <Select
                                label={i18n.measureType}
                                options={uniqBy(orderBy(pluviometerStatistics, ['order', 'typeId'], 'asc').map(m => ({ value: m.typeId, label: m.label })), 'value')}
                                value={newStat}
                                onChange={t => {
                                    setNewStat(t)
                                    setIsValidateActive(true)
                                }}
                                noSort
                                noNullValue
                            />
                        </Grid>
                    )}
                    {newTypeChart === NORMAL_COMPARISON && (
                        <Grid size={4}>
                            <SimpleDatePicker
                                label={i18n.startDate}
                                value={tmpDateComparedNormal}
                                onChange={t => {
                                    setTmpDateComparedNormal(t)
                                    setIsValidateActive(true)
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </StyledFieldSet>
            <Grid container justifyContent='center' alignItems='center' sx={{ paddingTop: '8px' }}>
                <Button
                    tooltip={i18n.apply}
                    onClick={onSave}
                    icon='border_color'
                    disabled={disabledButton}
                    className={`btn-floating btn-large ${!disabledButton ? 'pulse' : ''}`}
                />
            </Grid>
            {typeChart === GENERIC && (
                <>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.advancedOptions}</StyledLegend>
                        <Grid container spacing={1}>
                            <Grid size={12}>
                                <Checkbox
                                    label={i18n.enveloppeMinMax}
                                    checked={showEnveloppe}
                                    onChange={() => setShowEnveloppe(!showEnveloppe)}
                                />
                            </Grid>
                            {showEnveloppe && (
                                <Grid size={12} sx={{ paddingLeft: 4 }}>
                                    <Select
                                        label={i18n.modeGrouping}
                                        options={ENVELOPE_GROUP_OPTIONS}
                                        value={envelopeGroup}
                                        onChange={setEnvelopeGroup}
                                        noNullValue
                                        noSort
                                    />
                                </Grid>
                            )}
                            {showEnveloppe && envelopeGroup === 'auto' && envelopeGroupLabel && (
                                <Grid size={12} sx={{ paddingLeft: 4, fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)', fontStyle: 'italic' }}>
                                    {i18n.computedOn} : {envelopeGroupLabel}
                                </Grid>
                            )}
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>
                            <Grid container spacing={1} alignItems='center'>
                                {i18n.frequencyStats}
                                <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.descriptionCumulFrequencyStat}</span>}>
                                    <InfoIcon fontSize='small' />
                                </Tooltip>
                            </Grid>
                        </StyledLegend>
                        {freChecksHeight.length ? freChecksHeight : i18n.noDataOverThisPeriod}
                        <Grid container justifyContent='center' alignItems='center' sx={{ paddingTop: '8px', width: '100%' }}>
                            <Button
                                tooltip={i18n.apply}
                                onClick={onSaveFrequencyStats}
                                icon='border_color'
                                className='btn-floating btn-large'
                            />
                        </Grid>
                    </StyledFieldSet>
                </>
            )}
        </>
    )
}

ChoiceTypeChart.propTypes = {
    typeChart: PropTypes.string,
    setTypeChart: PropTypes.func,
    pluviometerStatistics: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluviometerStats)),
    stat: PropTypes.number,
    setStat: PropTypes.func,
    dateComparedNormal: PropTypes.number,
    setDateComparedNormal: PropTypes.func,
    showEnveloppe: PropTypes.bool,
    setShowEnveloppe: PropTypes.func,
    paramsFrequencyStats: PropTypes.shape({
        histoYears: PropTypes.number,
    }),
    setParamsFrequencyStats: PropTypes.func,
    envelopeGroupLabel: PropTypes.string,
    envelopeGroup: PropTypes.string,
    setEnvelopeGroup: PropTypes.func,
    tab: PropTypes.string,
}

export default ChoiceTypeChart
